/**
 * MTOD Patient View Design Tokens
 * 
 * This file defines the design tokens used throughout the application.
 * These tokens provide a consistent design system that can be applied across
 * different themes (light/dark) and components.
 */

// Color Tokens
export const colors = {
  // Primary palette
  primary: {
    50: 'var(--primary-50)',
    100: 'var(--primary-100)',
    200: 'var(--primary-200)',
    300: 'var(--primary-300)',
    400: 'var(--primary-400)',
    500: 'var(--primary-500)',
    600: 'var(--primary-600)',
    700: 'var(--primary-700)',
    800: 'var(--primary-800)',
    900: 'var(--primary-900)',
  },
  
  // Clinical data palette
  clinical: {
    50: 'var(--clinical-50)',
    100: 'var(--clinical-100)',
    200: 'var(--clinical-200)',
    300: 'var(--clinical-300)',
    400: 'var(--clinical-400)',
    500: 'var(--clinical-500)',
    600: 'var(--clinical-600)',
    700: 'var(--clinical-700)',
    800: 'var(--clinical-800)',
    900: 'var(--clinical-900)',
  },
  
  // Alert palette
  alert: {
    50: 'var(--alert-50)',
    100: 'var(--alert-100)',
    200: 'var(--alert-200)',
    300: 'var(--alert-300)',
    400: 'var(--alert-400)',
    500: 'var(--alert-500)',
    600: 'var(--alert-600)',
    700: 'var(--alert-700)',
    800: 'var(--alert-800)',
    900: 'var(--alert-900)',
  },
  
  // Neutral palette
  neutral: {
    50: 'var(--neutral-50)',
    100: 'var(--neutral-100)',
    200: 'var(--neutral-200)',
    300: 'var(--neutral-300)',
    400: 'var(--neutral-400)',
    500: 'var(--neutral-500)',
    600: 'var(--neutral-600)',
    700: 'var(--neutral-700)',
    800: 'var(--neutral-800)',
    900: 'var(--neutral-900)',
  },
  
  // System states
  success: 'var(--success)',
  warning: 'var(--warning)',
  error: 'var(--error)',
  info: 'var(--info)',
  
  // Semantic colors
  background: {
    primary: 'var(--background-primary)',
    secondary: 'var(--background-secondary)',
    tertiary: 'var(--background-tertiary)',
  },
  text: {
    primary: 'var(--text-primary)',
    secondary: 'var(--text-secondary)',
    tertiary: 'var(--text-tertiary)',
    disabled: 'var(--text-disabled)',
  },
  border: {
    default: 'var(--border-default)',
    light: 'var(--border-light)',
    focus: 'var(--border-focus)',
  },
};

// Typography Tokens
export const typography = {
  fontFamily: {
    sans: 'var(--font-sans)',
    serif: 'var(--font-serif)',
    mono: 'var(--font-mono)',
  },
  fontSize: {
    xs: 'var(--font-size-xs)',      // 0.75rem
    sm: 'var(--font-size-sm)',      // 0.875rem
    base: 'var(--font-size-base)',  // 1rem
    lg: 'var(--font-size-lg)',      // 1.125rem
    xl: 'var(--font-size-xl)',      // 1.25rem
    '2xl': 'var(--font-size-2xl)',  // 1.5rem
    '3xl': 'var(--font-size-3xl)',  // 1.875rem
    '4xl': 'var(--font-size-4xl)',  // 2.25rem
    '5xl': 'var(--font-size-5xl)',  // 3rem
    '6xl': 'var(--font-size-6xl)',  // 3.75rem
  },
  fontWeight: {
    thin: 'var(--font-weight-thin)',
    light: 'var(--font-weight-light)',
    normal: 'var(--font-weight-normal)',
    medium: 'var(--font-weight-medium)',
    semibold: 'var(--font-weight-semibold)',
    bold: 'var(--font-weight-bold)',
    extrabold: 'var(--font-weight-extrabold)',
  },
  lineHeight: {
    none: 'var(--line-height-none)',
    tight: 'var(--line-height-tight)',
    snug: 'var(--line-height-snug)',
    normal: 'var(--line-height-normal)',
    relaxed: 'var(--line-height-relaxed)',
    loose: 'var(--line-height-loose)',
  },
  letterSpacing: {
    tighter: 'var(--letter-spacing-tighter)',
    tight: 'var(--letter-spacing-tight)',
    normal: 'var(--letter-spacing-normal)',
    wide: 'var(--letter-spacing-wide)',
    wider: 'var(--letter-spacing-wider)',
    widest: 'var(--letter-spacing-widest)',
  },
};

// Spacing Tokens
export const spacing = {
  px: 'var(--spacing-px)',
  0: 'var(--spacing-0)',
  0.5: 'var(--spacing-0-5)',
  1: 'var(--spacing-1)',
  1.5: 'var(--spacing-1-5)',
  2: 'var(--spacing-2)',
  2.5: 'var(--spacing-2-5)',
  3: 'var(--spacing-3)',
  3.5: 'var(--spacing-3-5)',
  4: 'var(--spacing-4)',
  5: 'var(--spacing-5)',
  6: 'var(--spacing-6)',
  7: 'var(--spacing-7)',
  8: 'var(--spacing-8)',
  9: 'var(--spacing-9)',
  10: 'var(--spacing-10)',
  11: 'var(--spacing-11)',
  12: 'var(--spacing-12)',
  14: 'var(--spacing-14)',
  16: 'var(--spacing-16)',
  20: 'var(--spacing-20)',
  24: 'var(--spacing-24)',
  28: 'var(--spacing-28)',
  32: 'var(--spacing-32)',
  36: 'var(--spacing-36)',
  40: 'var(--spacing-40)',
  44: 'var(--spacing-44)',
  48: 'var(--spacing-48)',
  52: 'var(--spacing-52)',
  56: 'var(--spacing-56)',
  60: 'var(--spacing-60)',
  64: 'var(--spacing-64)',
  72: 'var(--spacing-72)',
  80: 'var(--spacing-80)',
  96: 'var(--spacing-96)',
};

// Shadow Tokens
export const shadows = {
  sm: 'var(--shadow-sm)',
  md: 'var(--shadow-md)',
  lg: 'var(--shadow-lg)',
  xl: 'var(--shadow-xl)',
  '2xl': 'var(--shadow-2xl)',
  inner: 'var(--shadow-inner)',
  none: 'var(--shadow-none)',
};

// Border Radius Tokens
export const borderRadius = {
  none: 'var(--radius-none)',
  sm: 'var(--radius-sm)',
  md: 'var(--radius-md)',
  lg: 'var(--radius-lg)',
  xl: 'var(--radius-xl)',
  '2xl': 'var(--radius-2xl)',
  '3xl': 'var(--radius-3xl)',
  full: 'var(--radius-full)',
};

// Z-index Tokens
export const zIndices = {
  0: 'var(--z-0)',
  10: 'var(--z-10)',
  20: 'var(--z-20)',
  30: 'var(--z-30)',
  40: 'var(--z-40)',
  50: 'var(--z-50)',
  auto: 'var(--z-auto)',
};

// Animation Tokens
export const animations = {
  duration: {
    faster: 'var(--duration-faster)',
    fast: 'var(--duration-fast)',
    normal: 'var(--duration-normal)',
    slow: 'var(--duration-slow)',
    slower: 'var(--duration-slower)',
  },
  easing: {
    easeIn: 'var(--ease-in)',
    easeOut: 'var(--ease-out)',
    easeInOut: 'var(--ease-in-out)',
    linear: 'var(--ease-linear)',
  },
};

// Export all tokens as a unified design system
export const tokens = {
  colors,
  typography,
  spacing,
  shadows,
  borderRadius,
  zIndices,
  animations,
}; 